import { makeStyles } from "@fluentui/react-components";
import {
  GREEN_INK,
  GREEN_SOFT_BG,
} from "apps/project-iq/utils/diagnosticUtils";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

const BUTTON_HEIGHT = "36px";
const BUTTON_BORDER_RADIUS = "9px";
const STEP_BORDER_RADIUS = "10px";
const BADGE_FONT_SIZE = "11px";
const OPEN_LINK_ICON_SIZE = "36px";

export const useDiagnosticRunStyles = makeStyles({
  card: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: "28px",
    fontFamily: projectIqTokens.fontFamily,
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "6px",
  },

  heading: {
    fontSize: "20px",
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: 0,
  },

  subtitle: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    marginTop: "4px",
    marginBottom: "20px",
  },

  configuredBadge: {
    fontSize: BADGE_FONT_SIZE,
    fontWeight: 800,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: GREEN_INK,
    backgroundColor: GREEN_SOFT_BG,
    padding: "4px 10px",
    borderRadius: "6px",
    lineHeight: "1",
  },

  stepList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  stepRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "14px 16px",
    borderTop: `1px solid ${projectIqTokens.line}`,
    borderRight: `1px solid ${projectIqTokens.line}`,
    borderBottom: `1px solid ${projectIqTokens.line}`,
    borderLeft: `4px solid ${projectIqTokens.violet}`,
    borderRadius: STEP_BORDER_RADIUS,
  },

  stepBody: {
    flex: 1,
  },

  stepTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: projectIqTokens.ink,
    margin: 0,
  },

  stepDesc: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    marginTop: "2px",
  },

  openLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: BUTTON_HEIGHT,
    padding: "0 16px",
    borderRadius: BUTTON_BORDER_RADIUS,
    backgroundColor: projectIqTokens.violet,
    color: projectIqTokens.sidebarTextStrong,
    fontSize: "13px",
    fontWeight: 700,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
    flexShrink: 0,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.ink}`,
      outlineOffset: "2px",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: "0.65",
    },
  },

  openLinkCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
    flexShrink: 0,
    "--arrow-color": projectIqTokens.faint,
    transition: `box-shadow ${designTokens.transition.fast}, transform ${designTokens.transition.fast}`,
    ":hover": {
      boxShadow: `0 0 0 1px ${projectIqTokens.tealLine}`,
      transform: "translateY(-1px)",
      "--arrow-color": projectIqTokens.teal,
    },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.ink}`,
      outlineOffset: "2px",
    },
  },

  openLinkCardIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: OPEN_LINK_ICON_SIZE,
    height: OPEN_LINK_ICON_SIZE,
    minWidth: OPEN_LINK_ICON_SIZE,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.tealSoft,
    color: projectIqTokens.teal,
    fontSize: "18px",
    flexShrink: 0,
  },

  openLinkCardContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  openLinkCardTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    margin: 0,
  },

  openLinkCardSubtext: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.teal,
    margin: 0,
  },

  openLinkCardArrow: {
    display: "flex",
    alignItems: "center",
    color: "var(--arrow-color)" as string,
    flexShrink: 0,
  },

  errorText: {
    fontSize: "13px",
    color: projectIqTokens.rust,
    marginTop: "12px",
  },

  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: `1px solid ${projectIqTokens.line}`,
  },

  footerText: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
  },

  markConfiguredButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: BUTTON_HEIGHT,
    padding: "0 16px",
    borderRadius: BUTTON_BORDER_RADIUS,
    backgroundColor: projectIqTokens.teal,
    color: projectIqTokens.sidebarTextStrong,
    fontSize: "13px",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
    flexShrink: 0,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.ink}`,
      outlineOffset: "2px",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: "0.65",
    },
  },
});
